/* Shared kernel: event bus, money formatting, lazy module loading,
   Section Rendering API fetch. Loaded as a module on every page. */

export const bus = {
  on(type, handler) {
    document.addEventListener(type, handler);
    return () => document.removeEventListener(type, handler);
  },
  emit(type, detail) {
    document.dispatchEvent(new CustomEvent(type, { detail, bubbles: false }));
  },
};

const MONEY_FORMAT = window.__theme?.moneyFormat || '${{amount}}';

export function formatMoney(cents, format = MONEY_FORMAT) {
  if (typeof cents === 'string') cents = cents.replace('.', '');
  const value = parseInt(cents, 10) || 0;
  const placeholder = /\{\{\s*(\w+)\s*\}\}/;

  function delimited(number, precision = 2, thousands = ',', decimal = '.') {
    const fixed = (number / 100).toFixed(precision);
    const [whole, part] = fixed.split('.');
    const grouped = whole.replace(/(\d)(?=(\d{3})+$)/g, `$1${thousands}`);
    return part ? `${grouped}${decimal}${part}` : grouped;
  }

  const styles = {
    amount: delimited(value, 2),
    amount_no_decimals: delimited(value, 0),
    amount_with_comma_separator: delimited(value, 2, '.', ','),
    amount_no_decimals_with_comma_separator: delimited(value, 0, '.', ','),
  };

  /* Server-side prices render with money_without_trailing_zeros ("$99"), so
     strip a zero-cents suffix (".00" / ",00") to match; non-zero cents kept. */
  return format
    .replace(placeholder, (_, style) => styles[style] ?? styles.amount)
    .replace(/([.,])00(?!\d)/, '');
}

/* Defers a module until its host element nears the viewport or the user
   interacts with it, whichever comes first. Usage:
   <div data-lazy-module="{{ 'media-gallery.js' | asset_url }}">…</div> */
const loaded = new Set();

function importOnce(src) {
  if (loaded.has(src)) return;
  loaded.add(src);
  /* A failed import is un-marked so the next trigger retries it. */
  import(src).catch(() => loaded.delete(src));
}

export function lazyDefine(root = document) {
  const hosts = root.querySelectorAll('[data-lazy-module]');
  if (!hosts.length) return;

  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        io.unobserve(entry.target);
        importOnce(entry.target.dataset.lazyModule);
      }
    },
    { rootMargin: '200px' }
  );

  for (const host of hosts) {
    io.observe(host);
    host.addEventListener('pointerdown', () => importOnce(host.dataset.lazyModule), { once: true });
    host.addEventListener('focusin', () => importOnce(host.dataset.lazyModule), { once: true });
  }
}

/* Fetches one or more sections via the Section Rendering API and returns
   { sectionId: htmlString }. */
export async function fetchSections(sectionIds, url = window.location.pathname) {
  const ids = Array.isArray(sectionIds) ? sectionIds : [sectionIds];
  const target = new URL(url, window.location.origin);
  target.searchParams.set('sections', ids.join(','));
  /* No Accept: application/json here — on /collections/* URLs that header
     makes Shopify return the collection's JSON representation and silently
     ignore the sections param. The SRA replies JSON on its own. */
  const response = await fetch(target);
  if (!response.ok) throw new Error(`Section render failed: ${response.status}`);
  return response.json();
}

document.addEventListener('DOMContentLoaded', () => lazyDefine());
document.addEventListener('shopify:section:load', (event) => lazyDefine(event.target));
